import { useEffect, useRef } from 'react'
import { useGlobalSearchTarget } from './useGlobalSearchHighlight'

/**
 * Paginated lists only render the current page, so a global-search result whose row lives on
 * another page never mounts — the scroll-highlight then finds nothing. This hook locates the
 * highlighted row in the FULL (pre-pagination) list and jumps to the page that contains it, so
 * the row mounts and `useGlobalSearchScrollHighlight` can scroll to + flash it.
 *
 * `onPage` receives the zero-based page index. Pass `pageOffset: 1` for 1-based pagers.
 * Keyed on the navigation, so it re-jumps when the same entity is re-selected (even after the user
 * has paged away), and only fires while a target is active (auto-clears ~5s after navigation) so it
 * never fights the user's manual pagination afterwards.
 */
export function useGlobalSearchPageJump<T>(items: T[], getId: (item: T) => string, pageSize: number, onPage: (page: number) => void, pageOffset = 0): void {
    const target = useGlobalSearchTarget()
    // Refs so unstable inline callbacks don't re-trigger the effect every render.
    const getIdRef = useRef(getId)
    getIdRef.current = getId
    const onPageRef = useRef(onPage)
    onPageRef.current = onPage

    useEffect(() => {
        if (!target || pageSize <= 0) return
        const index = items.findIndex((item) => getIdRef.current(item) === target.id)
        if (index >= 0) onPageRef.current(Math.floor(index / pageSize) + pageOffset)
    }, [target, items, pageSize, pageOffset])
}
